<script setup lang="ts">
import { useI18n } from 'vue-i18n'
const { t } = useI18n()
import { useLogger } from 'vue-logger-plugin'
import { onMounted, onUnmounted, ref, computed, h, nextTick, reactive } from 'vue'
import FindEventContactsControl from './FindEventContactsControl.vue'
import { setStoreLogger } from '../../logger.ts'
const logger = useLogger()
setStoreLogger(logger)
logger.debug('HostDashboard.vue')

import {
  EventMasterOrderGet,
  EventGuestGetList,
  EventGuestDelete,
  EventGuestSendInvitation,
  EventGuestUpdate,
  EventGuestCreate,
} from '../../services/EventService'
import { HostPortalEventGet } from '../../api/Event'
import { EarnedDiscountsToDescriptionList } from '../../services/CartService'
import {
  GetBeeNumberAuthenicatedUser,
  GetAuthenticatedUserClassId,
} from '../../stores/SettingsStore'
import { CONSULTANT_CLASS_ID } from '../../constants/Classes'

import type { IEventOrderResponse } from '../../types/EventTypes'
import type { IAddress, IPhoneInput, IPhoneOutput } from '../../types/CommonTypes'
import type { IAoeCustomer } from '../../types/CustomerTypes.js'
import { Button as KButton } from '@progress/kendo-vue-buttons'
import { Checkbox as KCheckBox, type CheckboxChangeEvent } from '@progress/kendo-vue-inputs'
import { Popup } from '@progress/kendo-vue-popup'
import { generateNoEmailAddress } from '../../utils/noEmailHelper'
import { HostPortalAddressBookCreate } from '../../services/AddressBookService'
import { useExistingAccountConfirm } from '../../composables/useExistingAccountConfirm'
const { saveWithExistingAccountConfirm } = useExistingAccountConfirm()
import { GetCodesFromLocalStorageOrApi } from '../../services/CountryCultureService'
import { GetCountriesFromLocalStorageOrApi } from '../../services/CountryCultureService'
import { FormatCurrency } from '../../services/currencyFormatter.ts'
import { getDateFormatLocale } from '../../api/Common'
import { handleError } from '../../services/ErrorService.ts'
import { SetIsLoading } from '@/services/AoeHelpers.ts'
import Swal from 'sweetalert2'

interface ICategoryCode {
  id: string
  text: string
  categoryId: string
}
import type { IAoeCartEarnedDiscount, IAoeCartGamification } from '../../types/CartTypes'

import PhoneInputControl from '../PhoneInputControl.vue'

const eventIdToken = ref<string | null>(null)
const viewModel = ref<IHostDashBoard | null>(null)
const beeNumberAuth = ref<number | null>(0)
const authenticatedUserClassId = ref<string | null>(null)

const canImportGuests = computed(() => {
  if (!beeNumberAuth.value) return false
  return authenticatedUserClassId.value?.toUpperCase() === CONSULTANT_CLASS_ID
})

// Password protection state
const showPasswordPrompt = ref<boolean>(false)
const enteredPassword = ref<string>('')
const storedPassword = ref<string>('')
const passwordValidated = ref<boolean>(false)

// Returns the correct chip class for guest status
function getStatusChipClass(status: string) {
  if (status === 'Pending Response') return 'pending';
  if (status === 'Going') return 'going';
  if (status === "Can't Go") return 'cantgo';
  return '';
}

interface IHostDashBoard {
  WelcomeMessage: string | null
  InvitedGuests: number | null
  AttendingGuests: number | null
  EventName: string | null
  EventDate: Date | null
  EventEndDate: Date | null
  Guests: IHostDashBoardGuest[]
  EventIdToken: string
  EarnedDiscounts?: IAoeCartEarnedDiscount[]
  EarnedDiscountDescriptions?: string[]
  DefaultCountryCode?: string
  Address?: IAddress
  Gamification?: IAoeCartGamification[]
  HostCartTotal?: number
}
interface IHostDashBoardGuest {
  Name: string
  EmailAddress: string
  OrderTotal: number | null
  OrderStatus: string
  CodeTextInvitationStatus: string
  CodeIdInvitationStatus: string
  HideRemoval?: boolean
  EventContactId: number
}

const invitationStatuses = ref<ICategoryCode[]>([])
const openMenuGuestId = ref<number | null>(null)
const showStatusSubmenu = ref<number | null>(null)

// Pagination state
const skip = ref<number>(0)
const pageSize = ref<number>(10) // Show 10 guests per page

interface PagerProps {
  total: number;
  pageSize: number;
  skip: number;
  buttonCount?: number;
  pageChange: (params: { skip: number; take: number }) => void;
}

// Total sales computed property
const totalSales = computed(() => {
  if (!viewModel.value?.Guests) return 0;
  return viewModel.value.Guests
    .filter(g => typeof g.OrderTotal === 'number')
    .reduce((sum, g) => sum + (g.OrderTotal as number), 0);
});


// Next gamification level (first unearned OrderTotal/PartyOrderTotal level)
const nextLevelGamification = computed(() => {
  const gams = viewModel.value?.Gamification
  if (!gams || gams.length === 0) return null
  const earnedIds = new Set((viewModel.value?.EarnedDiscounts ?? []).map(ed => ed.poeDiscPlanLevelDiscId))
  return gams.find(gm => {
    if (!gm.requirements || gm.requirements.length === 0) return false
    const kw = gm.requirements[0].systemKeyword
    if (kw !== 'OrderTotal' && kw !== 'PartyOrderTotal') return false
    return !gm.awards.some(a => earnedIds.has(a.poeDiscPlanLevelDiscId))
  }) ?? null
})

// Donut chart circumference helpers (r=52)
const DONUT_RADIUS = 52
const DONUT_CIRC = computed(() => 2 * Math.PI * DONUT_RADIUS)
const donutProgress = computed(() => {
  const gam = nextLevelGamification.value
  if (!gam) {
    // No next level — if at least one reward has been earned, show full circle
    return (viewModel.value?.EarnedDiscounts?.length ?? 0) > 0 ? 1 : 0
  }
  if (!gam.requirements[0]) return 0
  const total = gam.requirements[0].totalAmount
  const difference = gam.requirements[0].difference
  if (total <= 0) return 1
  return Math.min(1, Math.max(0, (total - difference) / total))
})
const donutDashOffset = computed(() => {
  return DONUT_CIRC.value * (1 - donutProgress.value)
})

// Paginated guests data
const pagedGuests = computed(() => {
  if (!viewModel.value?.Guests) return [];
  return viewModel.value.Guests.slice(skip.value, skip.value + pageSize.value);
});

// Custom pagination component
function customPagerRender(pagerProps: PagerProps) {
  const total = pagerProps.total;
  const pageSize = pagerProps.pageSize;
  const skip = pagerProps.skip;
  const buttonCount = pagerProps.buttonCount || 5;
  const pageCount = Math.ceil(total / pageSize);
  const currentPage = Math.floor(skip / pageSize) + 1;

  if (pageCount <= 1) return null; // Don't show pagination if only one page

  const pages = [];
  let start = Math.max(1, currentPage - Math.floor(buttonCount / 2));
  const end = Math.min(pageCount, start + buttonCount - 1);
  if (end - start < buttonCount - 1) {
    start = Math.max(1, end - buttonCount + 1);
  }

  // Previous button
  pages.push(h('button', {
    class: ['guest-pager-btn', 'guest-pager-nav', { 'disabled': currentPage === 1 }],
    onClick: () => currentPage > 1 && pagerProps.pageChange({ skip: (currentPage - 2) * pageSize, take: pageSize }),
    type: 'button',
    disabled: currentPage === 1,
    'aria-label': t('pagination.previousPage'),
  }, '‹'));

  // First page
  if (start > 1) {
    pages.push(h('button', {
      class: ['guest-pager-btn', { 'k-selected': currentPage === 1 }],
      onClick: () => pagerProps.pageChange({ skip: 0, take: pageSize }),
      type: 'button',
      'aria-label': t('pagination.goToPage', { page: 1 }),
    }, '1'));
    if (start > 2) {
      pages.push(h('span', { class: 'guest-pager-ellipsis' }, t('pagination.ellipsis')));
    }
  }

  // Page range
  for (let i = start; i <= end; ++i) {
    pages.push(h('button', {
      class: ['guest-pager-btn', { 'k-selected': i === currentPage }],
      onClick: () => pagerProps.pageChange({ skip: (i - 1) * pageSize, take: pageSize }),
      type: 'button',
      disabled: i === currentPage,
      'aria-label': t('pagination.goToPage', { page: i }),
    }, String(i)));
  }

  // Last page
  if (end < pageCount) {
    if (end < pageCount - 1) {
      pages.push(h('span', { class: 'guest-pager-ellipsis' }, t('pagination.ellipsis')));
    }
    pages.push(h('button', {
      class: ['guest-pager-btn', { 'k-selected': currentPage === pageCount }],
      onClick: () => pagerProps.pageChange({ skip: (pageCount - 1) * pageSize, take: pageSize }),
      type: 'button',
      'aria-label': t('pagination.goToPage', { page: pageCount }),
    }, String(pageCount)));
  }

  // Next button
  pages.push(h('button', {
    class: ['guest-pager-btn', 'guest-pager-nav', { 'disabled': currentPage === pageCount }],
    onClick: () => currentPage < pageCount && pagerProps.pageChange({ skip: currentPage * pageSize, take: pageSize }),
    type: 'button',
    disabled: currentPage === pageCount,
    'aria-label': t('pagination.nextPage'),
  }, '›'));

  return h('div', { class: 'guest-pager-bar' }, pages);
}

function onPageChange(e: { skip: number }) {
  skip.value = e.skip;
}

const showAddGuest = ref<boolean>(false)
const showImportGuests = ref<boolean>(false)

// Form values
const addGuestInitialValues = ref({
  firstName: '',
  lastName: '',
  emailAddress: '',
} as IAoeCustomer)

// Phone state
const guestPhoneControl = ref<InstanceType<typeof PhoneInputControl> | null>(null)

interface IGuestPhoneCountry {
  text: string
  id: string
}

const guestPhoneCountries = ref<IGuestPhoneCountry[]>([])
const guestPhoneModel = ref<IPhoneOutput | null>(null)

// Manual validation errors
const errors = reactive({
  firstName: '',
  lastName: '',
  emailAddress: '',
})

// "No Email" checkbox (TTG-9674): fills in a unique placeholder address instead of a shared fake one like
// noemail@mail.de, which would link every such guest to the same existing account
const hasNoEmail = ref(false)

function noEmailCheckboxHandler(event: CheckboxChangeEvent) {
  hasNoEmail.value = !!event.value
  addGuestInitialValues.value.emailAddress = hasNoEmail.value ? generateNoEmailAddress() : ''
  errors.emailAddress = ''
}

// Validation functions
const validateFirstName = (): boolean => {
  const nameRegex = /^[\p{L}\s'-]+$/u

  if (!addGuestInitialValues.value.firstName?.trim()) {
    errors.firstName = t('consultantCorner.contactDetails.FirstNameRequired')
    return false
  } else if (!nameRegex.test(addGuestInitialValues.value.firstName)) {
    errors.firstName = t('validators.nameField', { fieldName: t('columns.firstName') })
    return false
  } else if (addGuestInitialValues.value.firstName.trim().length < 2) {
    errors.firstName = t('validators.fieldMinLength', { fieldName: t('columns.firstName'), minimumLength: 2 })
    return false
  } else if (addGuestInitialValues.value.firstName.length > 50) {
    errors.firstName = t('validators.fieldLength', { fieldName: t('columns.firstName'), maximumLength: 50 })
    return false
  }

  errors.firstName = ''
  return true
}

const validateLastName = (): boolean => {
  const nameRegex = /^[\p{L}\s'-]+$/u

  if (!addGuestInitialValues.value.lastName?.trim()) {
    errors.lastName = t('consultantCorner.contactDetails.LastNameRequired')
    return false
  } else if (!nameRegex.test(addGuestInitialValues.value.lastName)) {
    errors.lastName = t('validators.nameField', { fieldName: t('columns.lastName') })
    return false
  } else if (addGuestInitialValues.value.lastName.trim().length < 2) {
    errors.lastName = t('validators.fieldMinLength', { fieldName: t('columns.lastName'), minimumLength: 2 })
    return false
  } else if (addGuestInitialValues.value.lastName.length > 50) {
    errors.lastName = t('validators.fieldLength', { fieldName: t('columns.lastName'), maximumLength: 50 })
    return false
  }

  errors.lastName = ''
  return true
}

const validateEmailAddress = (): boolean => {
  const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/

  if (!addGuestInitialValues.value.emailAddress?.trim()) {
    errors.emailAddress = t('consultantCorner.contactDetails.EmailRequired')
    return false
  } else if (!emailRegex.test(addGuestInitialValues.value.emailAddress)) {
    errors.emailAddress = t('consultantCorner.contactDetails.EmailInvalid')
    return false
  }

  errors.emailAddress = ''
  return true
}

// Phone helper functions
function newGuestPhoneModel(): IPhoneOutput {
  return {
    number: '',
    countryCode: viewModel.value?.DefaultCountryCode || '',
    formattedNumber: '',
    codeIdPhoneType: 'HOME',
    extension: null,
  }
}

function loadGuestPhoneCountries() {
  GetCountriesFromLocalStorageOrApi().then(data => {
    if (data?.items) {
      const list: IGuestPhoneCountry[] = []
      for (const country of data.items) {
        list.push({ text: country.countryCode, id: country.countryCode })
      }
      list.sort((a, b) => a.text.localeCompare(b.text))
      guestPhoneCountries.value = list
      if (!guestPhoneModel.value || !guestPhoneModel.value.number) {
        guestPhoneModel.value = newGuestPhoneModel()
      }
    }
  })
}

// Toggle menu open/closed
function toggleMenu(guestId: number, event?: MouseEvent) {
  if (openMenuGuestId.value === guestId) {
    openMenuGuestId.value = null
    showStatusSubmenu.value = null
  } else {
    openMenuGuestId.value = guestId
    showStatusSubmenu.value = null

    // Check if menu should open upward
    if (event) {
      nextTick(() => {
        const button = event.currentTarget as HTMLElement
        const menuContainer = button.parentElement as HTMLElement
        const dropdown = menuContainer?.querySelector('.menu-dropdown') as HTMLElement

        if (dropdown) {
          const rect = button.getBoundingClientRect()
          // Wait a bit for the dropdown to render and get actual height
          setTimeout(() => {
            const dropdownHeight = dropdown.offsetHeight || 250
            const spaceBelow = window.innerHeight - rect.bottom
            const spaceAbove = rect.top
            const buffer = 125 // Extra buffer space for pagination, etc.

            logger.debug('Menu positioning:', { dropdownHeight, spaceBelow, spaceAbove, buffer, shouldFlip: spaceBelow < (dropdownHeight + buffer) && spaceAbove > spaceBelow })

            // If not enough space below (with buffer) but more space above, flip to top
            if (spaceBelow < (dropdownHeight + buffer) && spaceAbove > spaceBelow) {
              dropdown.classList.add('menu-dropdown-top')
            } else {
              dropdown.classList.remove('menu-dropdown-top')
            }
          }, 10)
        }
      })
    }
  }
}

// Show status submenu
function showChangeStatus(guestId: number) {
  showStatusSubmenu.value = guestId

  // Re-check positioning for the status submenu
  nextTick(() => {
    const menuContainer = document.querySelector(`[data-guest-id="${guestId}"]`) as HTMLElement
    if (menuContainer) {
      const dropdown = menuContainer.querySelector('.menu-dropdown') as HTMLElement
      const button = menuContainer.querySelector('.menu-trigger-btn') as HTMLElement

      if (dropdown && button) {
        const rect = button.getBoundingClientRect()
        const dropdownHeight = dropdown.offsetHeight || 250
        const spaceBelow = window.innerHeight - rect.bottom
        const spaceAbove = rect.top
        const buffer = 100 // Extra buffer space for pagination, etc.

        if (spaceBelow < (dropdownHeight + buffer) && spaceAbove > spaceBelow) {
          dropdown.classList.add('menu-dropdown-top')
        } else {
          dropdown.classList.remove('menu-dropdown-top')
        }
      }
    }
  })
}

// Go back to main menu
function backToMainMenu() {
  showStatusSubmenu.value = null
}

// Close menu when clicking outside
function handleClickOutside(event: MouseEvent) {
  const target = event.target as HTMLElement
  if (!target.closest('.guest-actions-menu')) {
    openMenuGuestId.value = null
    showStatusSubmenu.value = null
  }
}

onMounted(async () => {
  logger.debug('onMounted HostDashboard')

  const urlParams = new URLSearchParams(window.location.search)
  eventIdToken.value = urlParams.get('event')
  beeNumberAuth.value = await GetBeeNumberAuthenicatedUser()
  authenticatedUserClassId.value = await GetAuthenticatedUserClassId()
  SetIsLoading(true)

  GetCodesFromLocalStorageOrApi('INVITATIONSTATUS').then(data => {
    if (data?.items) {
      for (const code of data?.items) {
        invitationStatuses.value.push({ text: code.codeText, id: code.codeId, categoryId: 'INVITATIONSTATUS' })
      }
      invitationStatuses.value.sort((a, b) => {
        return a.text.localeCompare(b.text)
      })
    }
  })

  // Retrieve stored password from HostPortalEventGet
  try {
    const portalResponse = await HostPortalEventGet({ eventIdToken: eventIdToken.value || '' })
    if (portalResponse?.item?.password) {
      storedPassword.value = atob(portalResponse.item.password)
      // Show password prompt if a password is set
      SetIsLoading(false)
      showPasswordPrompt.value = true
    } else {
      // No password set, load dashboard directly
      passwordValidated.value = true
      loadData()
    }
  } catch (error) {
    logger.error('Failed to retrieve host portal settings:', error)
    // If we can't retrieve password settings, allow access
    passwordValidated.value = true
    loadData()
  }

  // Add click listener to close menus when clicking outside
  document.addEventListener('click', handleClickOutside)

  // Load phone countries for add guest form
  loadGuestPhoneCountries()

})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})

function handleMenuAction(action: string, dataItem: IHostDashBoardGuest, statusId?: string) {
  if (action === 'InvitationReminder') {
    openMenuGuestId.value = null // Close menu
    sendEventGuestInvitation(dataItem)
  } else if (action === 'ChangeStatus' && statusId) {
    openMenuGuestId.value = null // Close menu
    showStatusSubmenu.value = null
    changeEventGuestStatus(statusId, dataItem)
  } else if (action === 'RemoveGuest') {
    openMenuGuestId.value = null // Close menu
    deleteEventGuest(dataItem)
  }
}

function changeEventGuestStatus(
  codeIdInvitationStatus: string,
  dataItem: IHostDashBoardGuest,
) {
  logger.debug(
    'changeEventGuestStatus codeIdInvitationStatus:',
    codeIdInvitationStatus,
    'dataItem:',
    dataItem,
  )
  SetIsLoading(true)
  EventGuestUpdate({
    eventIdToken: viewModel.value!.EventIdToken,
    eventContactId: dataItem.EventContactId,
    codeIdInvitationStatus: codeIdInvitationStatus,
  })
    .then(resp => {
      if (resp) {
        loadData()
      }
    })
    .catch(async error => {
      SetIsLoading(false)
      await handleError(error, t)
    })
}

function sendEventGuestInvitation(dataItem: IHostDashBoardGuest) {
  logger.debug('sendEventGuestInvitation dataItem:', dataItem)
  SetIsLoading(true)
  EventGuestSendInvitation({
    eventIdToken: viewModel.value!.EventIdToken,
    eventContactId: dataItem.EventContactId,
  })
    .then(resp => {
      if (resp) {
        loadData()
      }
    })
    .catch(async error => {
      SetIsLoading(false)
      await handleError(error, t)
    })
}

function deleteEventGuest(dataItem: IHostDashBoardGuest) {
  logger.debug('deleteEventGuest dataItem:', dataItem)
  Swal.fire({
    title: t('alerts.DeleteConfirmTitle'),
    text: t('alerts.DeleteConfirm', [dataItem.Name]),
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: t('buttons.Yes'),
    cancelButtonText: t('buttons.No'),
  }).then(result => {
    if (result.isConfirmed) {
      SetIsLoading(true)
      EventGuestDelete({
        eventIdToken: viewModel.value!.EventIdToken,
        eventContactId: dataItem.EventContactId,
      })
        .then(resp => {
          if (resp) {
            loadData()
          }
        })
        .catch(async error => {
          SetIsLoading(false)
          await handleError(error, t)
        })
    }
  })
}

function showAddGuestEventHandler(event: Event) {
  event.preventDefault()
  showAddGuest.value = true
  addGuestInitialValues.value = { firstName: '', lastName: '', emailAddress: '' } as IAoeCustomer
  hasNoEmail.value = false
  guestPhoneModel.value = newGuestPhoneModel()
  errors.firstName = ''
  errors.lastName = ''
  errors.emailAddress = ''
}

function showImportGuestsEventHandler(event: Event) {
  event.preventDefault()
  showImportGuests.value = true
}

function closeImportGuestsHandler() {
  showImportGuests.value = false
}

function refreshGuestGrid() {
  showImportGuests.value = false
  loadData()
}
function addGuestSubmitClickHandler(event: Event) {
  event.preventDefault()
  logger.debug('addGuestSubmitClickHandler')

  let isValid = true

  // Validate individual fields
  if (!validateFirstName()) isValid = false
  if (!validateLastName()) isValid = false
  if (!validateEmailAddress()) isValid = false

  // Validate phone if number entered
  if (guestPhoneModel.value?.number && guestPhoneControl.value) {
    if (!guestPhoneControl.value.validate()) {
      isValid = false
    }
  }

  if (!isValid) {
    logger.debug('Form validation failed')
    return
  }

  let phoneNumber = { number: null, countryCode: null, codeIdPhoneType: null } as IPhoneInput
  const guestPhoneValue = guestPhoneModel.value?.number?.trim() ?? ''

  if (guestPhoneValue.length > 0) {
    phoneNumber = {
      number: guestPhoneValue,
      countryCode: guestPhoneModel.value?.countryCode || viewModel.value?.DefaultCountryCode,
      codeIdPhoneType: 'HOME',
    } as IPhoneInput
  }

  // If the email belongs to an existing account the host confirms it is the same person first; on cancel the
  // response is null, nothing is created and the form stays open so the email can be changed
  saveWithExistingAccountConfirm(confirmation => HostPortalAddressBookCreate({
    firstName: addGuestInitialValues.value.firstName,
    lastName: addGuestInitialValues.value.lastName,
    emailAddress: addGuestInitialValues.value.emailAddress,
    primaryPhone: phoneNumber,
    addressIdShipping: null,
    eventIdToken: viewModel.value!.EventIdToken,
    ...confirmation,
  }))
    .then(addressBookResp => {
      logger.debug('AddressBookCreate', addressBookResp)
      if (addressBookResp) {
        const addressBookId = addressBookResp.addressBookId
        SetIsLoading(true)
        EventGuestCreate({
          codeIdEventRole: 'G',
          eventIdToken: viewModel.value!.EventIdToken,
          guestBeeNumber: null,
          addressBookId: addressBookId,
        })
          .then(eventGuest => {
            SetIsLoading(false)
            if (eventGuest) {
              showAddGuest.value = false
              loadData()
            }
          })
          .catch(async error => {
            SetIsLoading(false)
            await handleError(error, t)
          })
      }
    })
    .catch(async error => {
      await handleError(error, t)
    })
}

function RewardsPdfClickHandler() {
  const pdfUrl = t('hostDashboard.RewardsPdfUrl')
  if (pdfUrl && pdfUrl.length > 0 && pdfUrl !== 'hostDashboard.RewardsPdfUrl') {
    window.open(pdfUrl, '_blank')
  }
}

function validatePassword() {
  if (enteredPassword.value === storedPassword.value) {
    passwordValidated.value = true
    showPasswordPrompt.value = false
    loadData()
  } else {
    Swal.fire({
      icon: 'error',
      title: t('alerts.Error'),
      text: t('hostDashboard.InvalidPassword'),
    })
    enteredPassword.value = ''
  }
}

function cancelPasswordPrompt() {
  showPasswordPrompt.value = false
  enteredPassword.value = ''
}

function loadData() {
  if (!eventIdToken.value) {
    logger.debug('No eventIdToken found in URL')
    SetIsLoading(false)
    return
  }

  EventMasterOrderGet({
    eventIdToken: eventIdToken.value,
  })
    .then(async resp => {
      viewModel.value = { EventIdToken: eventIdToken.value } as IHostDashBoard
      const eventResp = resp as IEventOrderResponse
      logger.debug('EventMasterOrderGet', eventResp)
      if (eventResp) {
        viewModel.value.DefaultCountryCode =
          eventResp.item.event.defaultCountryCode
        if (eventResp.item.event.host.firstName) {
          viewModel.value.WelcomeMessage = t('hostDashboard.Welcome', {
            firstName: eventResp.item.event.host.firstName,
          })
          viewModel.value.EventName = eventResp.item.event.eventName
          viewModel.value.EventDate = eventResp.item.event.eventDate
          viewModel.value.EventEndDate = eventResp.item.event.eventEndDate
        }
        if (eventResp.item.event.location) {
          viewModel.value.Address = eventResp.item.event.location
        }
        if (eventResp.item.carts) {
          const hostCart = eventResp.item.carts.find(item => item.isHostCart)
          if (hostCart) {
            if (hostCart.earnedDiscounts && hostCart.earnedDiscounts.length > 0) {
              viewModel.value.EarnedDiscounts = hostCart.earnedDiscounts
              viewModel.value.EarnedDiscountDescriptions =
                EarnedDiscountsToDescriptionList(t, hostCart.earnedDiscounts)
            }
            if (hostCart.gamification && hostCart.gamification.length > 0) {
              viewModel.value.Gamification = hostCart.gamification
            }
            viewModel.value.HostCartTotal = hostCart.total ?? 0
          }
        }
        EventGuestGetList({
          eventIdToken: eventIdToken.value!,
        })
          .then(resp => {
            logger.debug('EventGuestGetList', resp)
            if (resp && resp.items) {
              const guests = resp.items.filter(
                item => item.codeIdEventRole.codeId !== 'H',
              )
              viewModel.value!.InvitedGuests = guests.length
              viewModel.value!.AttendingGuests = guests.filter(
                item => item.codeIdInvitationStatus === 'YES',
              ).length
              viewModel.value!.Guests = guests.map(item => {
                return {
                  Name: item.firstName + ' ' + item.lastName,
                  EmailAddress: item.email,
                  OrderTotal: item.cartTotal == null ? item.orderTotal : item.cartTotal,
                  OrderStatus: item.orderTotal != null ? 'Submitted' : 'Pending',
                  CodeTextInvitationStatus: item.codeTextInvitationStatus,
                  CodeIdInvitationStatus: item.codeIdInvitationStatus,
                  HideRemoval: item.cartTotal == null,
                  EventContactId: item.eventContactId,
                }
              })
            }
            /* TEAU-1239: Mock data commented out - was for testing only
            if (viewModel.value?.Guests) {
              const mockGuests = [
                { Name: 'Klaus Müller', EmailAddress: 'k.muller@example.de', OrderTotal: 89.90, OrderStatus: 'Submitted', CodeTextInvitationStatus: 'Going', CodeIdInvitationStatus: 'YES', HideRemoval: false, EventContactId: 9991 },
                { Name: 'Marie Dupont', EmailAddress: 'm.dupont@example.fr', OrderTotal: 134.75, OrderStatus: 'Submitted', CodeTextInvitationStatus: 'Going', CodeIdInvitationStatus: 'YES', HideRemoval: false, EventContactId: 9992 },
                { Name: 'Anna Weber', EmailAddress: 'a.weber@example.at', OrderTotal: null, OrderStatus: 'Pending', CodeTextInvitationStatus: 'Pending Response', CodeIdInvitationStatus: 'PENDING', HideRemoval: true, EventContactId: 9993 },
              ]
              viewModel.value.Guests.push(...mockGuests)
            }
            */
            SetIsLoading(false)
            logger.debug('viewModel:', viewModel.value)
          })
          .catch(async error => {
            SetIsLoading(false)
            await handleError(error, t)
          })
      }
    })
    .catch(async error => {
      SetIsLoading(false)
      await handleError(error, t)
    })
}
</script>
<template>
  <div>
  <!-- Password Prompt Modal - at top of component -->
  <div v-if="showPasswordPrompt" class="modal-overlay" @click.self="cancelPasswordPrompt">
    <div class="password-modal">
      <h3>{{ t('hostDashboard.EnterPassword') }}</h3>
      <p>{{ t('hostDashboard.PasswordRequired') }}</p>
      <input
        v-model="enteredPassword"
        type="password"
        class="k-textbox"
        :placeholder="t('hostDashboard.PasswordPlaceholder')"
        @keyup.enter="validatePassword"
      />
      <div class="modal-buttons">
        <KButton @click="cancelPasswordPrompt">{{ t('buttons.Cancel') }}</KButton>
        <KButton theme-color="primary" @click="validatePassword">{{ t('buttons.Submit') }}</KButton>
      </div>
    </div>
  </div>

  <div class="host-dashboard">
    <div class="welcome-section" v-if="viewModel?.WelcomeMessage">
      <div class="welcome-title">
        {{ viewModel.WelcomeMessage }}
      </div>
      <div class="welcome-subtitle">
        {{ t('hostDashboard.WelcomeSubTitle') }}
      </div>
    </div>
    <div class="dashboard-cards">
      <!-- Party Details Card -->
      <div class="dashboard-card party-details-card">
  <div class="party-title">{{ t('hostDashboard.EventDetails') }}</div>
  <div class="party-event-name">
    {{ viewModel?.EventName }}
  </div>
  <div class="party-date-time-row">
  <span v-if="viewModel?.EventDate" class="party-date-time-pill">
    <span class="party-date">
      {{ viewModel?.EventDate ? new Date(viewModel.EventDate).toLocaleDateString(getDateFormatLocale(), { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }) : '' }}
    </span>
    <span class="party-date-divider"></span>
    <span>
      {{ viewModel?.EventDate ? new Date(viewModel.EventDate).toLocaleTimeString(getDateFormatLocale(), { hour: '2-digit', minute: '2-digit' }) : '' }}
      <span v-if="viewModel?.EventEndDate">
        {{ t('labels.Dash') }} {{ new Date(viewModel.EventEndDate).toLocaleTimeString(getDateFormatLocale(), { hour: '2-digit', minute: '2-digit' }) }}
      </span>
    </span>
  </span>
</div>

  <div class="party-location">
    <span class="party-location-pin" aria-hidden="true" style="margin-right:8px;">
      <svg width="20" height="20" fill="none" viewBox="0 0 24 24">
        <path fill="#222" d="M12 2C7.03 2 3 6.03 3 11c0 5.25 7.07 10.55 8.09 11.3a1 1 0 0 0 1.18 0C13.93 21.55 21 16.25 21 11c0-4.97-4.03-9-9-9zm0 15c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5z"/>
        <circle cx="12" cy="11" r="2.5" fill="#222"/>
      </svg>
    </span>
    <span v-if="viewModel?.Address">
      {{ [viewModel.Address.addressLine1, viewModel.Address.city, viewModel.Address.postalCode, viewModel.Address.countryCode].filter(Boolean).join(' ') }}
    </span>
  </div>
  <hr class="party-divider" />
  <div class="party-stats-row">
    <div class="party-stat">
      <div class="party-stat-label">{{ t('hostDashboard.Invited') }}</div>
      <div class="party-pill party-invited">{{ viewModel?.InvitedGuests }}</div>
    </div>
    <div class="party-stat">
      <div class="party-stat-label">{{ t('hostDashboard.Attending') }}</div>
      <div class="party-pill party-going">{{ viewModel?.AttendingGuests }}</div>
    </div>
  </div>
  <hr class="party-divider" />
</div>

      <!-- Total Sales Card -->
      <div class="dashboard-card total-sales-card">
        <div class="party-title">{{ t('hostDashboard.TotalSales') }}</div>
        <!-- Donut chart -->
        <div class="donut-chart-wrapper">
          <svg class="donut-svg" viewBox="0 0 120 120" width="140" height="140">
            <circle
              class="donut-track"
              cx="60" cy="60"
              :r="DONUT_RADIUS"
              fill="none"
              stroke="#f0e8e0"
              stroke-width="12"
            />
            <circle
              class="donut-fill"
              cx="60" cy="60"
              :r="DONUT_RADIUS"
              fill="none"
              stroke="url(#donut-gradient)"
              stroke-width="12"
              stroke-linecap="round"
              :stroke-dasharray="DONUT_CIRC"
              :stroke-dashoffset="donutDashOffset"
              transform="rotate(-90 60 60)"
            />
            <defs>
              <linearGradient id="donut-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stop-color="#e8956d"/>
                <stop offset="100%" stop-color="#c85a2a"/>
              </linearGradient>
            </defs>
          </svg>
          <div class="donut-center-text">
            <span class="donut-label">{{ t('hostDashboard.GuestSales') }}</span>
            <span class="donut-value">{{ FormatCurrency(totalSales, null) || '$0.00' }}</span>
          </div>
        </div>
        <!-- Next level message -->
        <div v-if="nextLevelGamification" class="next-level-message">
          <strong>{{ FormatCurrency(nextLevelGamification.requirements[0].difference, null) }}</strong>
          {{ t('hostDashboard.GuestSalesLeftToReach') }}
        </div>
        <div v-else-if="viewModel?.EarnedDiscounts && viewModel.EarnedDiscounts.length > 0" class="next-level-message next-level-complete">
          {{ t('hostDashboard.AllRewardsEarned') }}
        </div>
        <!-- About reward levels link -->
        <a class="about-rewards-link" @click.prevent="RewardsPdfClickHandler" href="#">
          {{ t('hostDashboard.AboutRewardLevels') }}
        </a>
      </div>
      <!-- My Rewards Card -->
      <div class="dashboard-card my-rewards-card">
        <div class="party-title">{{ t('hostDashboard.EventRewards') }}</div>
        <!-- Reward type tiles -->
        <div v-if="viewModel?.EarnedDiscounts && viewModel.EarnedDiscounts.length > 0" class="reward-tiles">
          <div
            v-for="(discount, index) in viewModel.EarnedDiscounts"
            :key="index"
            class="reward-tile"
            :class="discount.systemKeyword"
          >
            <div class="reward-tile-icon">
              <!-- Coupon -->
              <svg v-if="discount.systemKeyword === 'Coupon'" width="28" height="28" viewBox="0 0 24 24" fill="none">
                <path d="M2 9a1 1 0 011-1h1a2 2 0 000-4H3a1 1 0 01-1-1V2h20v1a1 1 0 01-1 1h-1a2 2 0 000 4h1a1 1 0 011 1v1H2V9z" stroke="currentColor" stroke-width="1.8" fill="none" stroke-linejoin="round"/>
                <path d="M2 13h20v9H2z" stroke="currentColor" stroke-width="1.8" fill="none" stroke-linejoin="round"/>
                <path d="M9 17h6" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
              </svg>
              <!-- DiscountedItem / DiscountedSkuFromList -->
              <svg v-else-if="discount.systemKeyword === 'DiscountedItem' || discount.systemKeyword === 'DiscountedSkuFromList'" width="28" height="28" viewBox="0 0 24 24" fill="none">
                <path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z" stroke="currentColor" stroke-width="1.8" fill="none"/>
                <circle cx="7" cy="7" r="1.5" fill="currentColor"/>
                <path d="M8 16l8-8" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
              </svg>
              <!-- FreeDollars / FreeDollarsOffOrder -->
              <svg v-else-if="discount.systemKeyword === 'FreeDollars' || discount.systemKeyword === 'FreeDollarsOffOrder'" width="28" height="28" viewBox="0 0 24 24" fill="none">
                <rect x="2" y="6" width="20" height="12" rx="2" stroke="currentColor" stroke-width="1.8" fill="none"/>
                <circle cx="12" cy="12" r="3" stroke="currentColor" stroke-width="1.8" fill="none"/>
                <path d="M6 9v6M18 9v6" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
              </svg>
              <!-- FreeProduct / SelectFreeProduct -->
              <svg v-else-if="discount.systemKeyword === 'FreeProduct' || discount.systemKeyword === 'SelectFreeProduct'" width="28" height="28" viewBox="0 0 24 24" fill="none">
                <rect x="3" y="8" width="18" height="13" rx="2" stroke="currentColor" stroke-width="1.8" fill="none"/>
                <path d="M3 8h18M12 8V21" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                <path d="M8 8C8 5.79 9.79 4 12 4s4 1.79 4 4" stroke="currentColor" stroke-width="1.8" fill="none"/>
              </svg>
              <!-- FreeShipping -->
              <svg v-else-if="discount.systemKeyword === 'FreeShipping'" width="28" height="28" viewBox="0 0 24 24" fill="none">
                <path d="M1 3h15v13H1z" stroke="currentColor" stroke-width="1.8" fill="none" stroke-linejoin="round"/>
                <path d="M16 8h4l3 3v5h-7V8z" stroke="currentColor" stroke-width="1.8" fill="none" stroke-linejoin="round"/>
                <circle cx="5.5" cy="18.5" r="2.5" stroke="currentColor" stroke-width="1.8" fill="none"/>
                <circle cx="18.5" cy="18.5" r="2.5" stroke="currentColor" stroke-width="1.8" fill="none"/>
              </svg>
              <!-- PercentageOffItem / PercentageOffOrder -->
              <svg v-else-if="discount.systemKeyword === 'PercentageOffItem' || discount.systemKeyword === 'PercentageOffOrder'" width="28" height="28" viewBox="0 0 24 24" fill="none">
                <circle cx="9" cy="9" r="2.5" stroke="currentColor" stroke-width="1.8" fill="none"/>
                <circle cx="15" cy="15" r="2.5" stroke="currentColor" stroke-width="1.8" fill="none"/>
                <path d="M5 19L19 5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
              </svg>
              <!-- Fallback -->
              <svg v-else width="28" height="28" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="1.8" fill="none"/>
                <path d="M12 8v4l3 3" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
              </svg>
            </div>
            <div class="reward-tile-text">
              <div class="reward-tile-label" v-html="discount.awardDescription"></div>
              <div class="reward-tile-value">
                <span v-if="discount.quantity && discount.quantity > 1" class="reward-tile-qty">{{ `x${discount.quantity} ` }}</span>{{ discount.awardMessage }}
              </div>
            </div>
          </div>
        </div>
        <div v-else class="no-rewards">
          <p class="no-rewards-text">{{ t('hostDashboard.NoRewardsYet') }}</p>
        </div>
      </div>
    </div>
    </div>
    <div>
      <div class="guest-list-container" style="background:var(--vt-c-white); border-radius:$radius-lg; box-shadow:0 1px 8px rgba(black, 0.1); padding:24px 18px 16px 18px;">
        <!-- <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:12px;"> -->
        <div class="row mb-2">
          <div class="guest-list-header col-sm-6 col-12">{{ t('hostDashboard.GuestList') }}</div>
          <div class="guest-list-header-buttons col-sm-6 col-12">
            <KButton
              v-if="canImportGuests"
              ref="btnImportGuests"
              id="btnImportGuests"
              class="btnImportGuests"
              :rounded="'large'"
              @click="showImportGuestsEventHandler($event)"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style="margin-right: 6px;">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
                <polyline points="14,2 14,8 20,8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
                <line x1="16" y1="13" x2="8" y2="13" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <line x1="16" y1="17" x2="8" y2="17" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <polyline points="10,9 9,9 8,9" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              {{ t('hostDashboard.Import') }}
            </KButton>
            <KButton
              ref="btnShowAddGuest"
              id="btnShowAddGuest"
              class="btnAddGuest"
              :rounded="'large'"
              @click="showAddGuestEventHandler($event)"
              >{{ t('hostDashboard.AddGuest') }}</KButton
            >
          </div>
        </div>
        <div class="guest-table-wrapper">
          <table class="guest-table">
            <thead>
            <tr>
              <th>{{ t('hostDashboard.GuestName') }}</th>
              <th>{{ t('hostDashboard.EmailAddress') }}</th>
              <th>{{ t('hostDashboard.GuestOrderTotal') }}</th>
              <th>{{ t('hostDashboard.OrderStatus') }}</th>
              <th>{{ t('hostDashboard.CodeTextInvitationStatus') }}</th>
              <th style="width:38px;"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="guest in pagedGuests" :key="guest.EventContactId">
              <td class="responsive-stacked" :data-label="t('hostDashboard.GuestName')">{{ guest.Name }}</td>
              <td class="responsive-stacked" :data-label="t('hostDashboard.EmailAddress')">{{ guest.EmailAddress }}</td>
              <td :data-label="t('hostDashboard.GuestOrderTotal')">
              {{
                typeof guest.OrderTotal === 'number'
                  ? FormatCurrency(guest.OrderTotal, null)
                  : (typeof guest.OrderTotal === 'string' && !isNaN(Number(guest.OrderTotal)))
                    ? FormatCurrency(Number(guest.OrderTotal), null)
                    : '-'
              }}
            </td>
              <td :data-label="t('hostDashboard.OrderStatus')">
                <span class="order-status-chip" :class="guest.OrderStatus === 'Submitted' ? 'order-submitted' : 'order-pending'">
                  {{ guest.OrderStatus === 'Submitted' ? t('hostDashboard.OrderSubmitted') : t('hostDashboard.OrderPending') }}
                </span>
              </td>
              <td :data-label="t('hostDashboard.CodeTextInvitationStatus')">
                <span
                  class="status-chip"
                  :class="getStatusChipClass(guest.CodeTextInvitationStatus)"
                >
                  {{ guest.CodeTextInvitationStatus }}
                </span>
              </td>
              <td class="guest-actions-cell" style="text-align:center;">
                <div class="guest-actions-menu" :data-guest-id="guest.EventContactId">
                  <button
                    class="menu-trigger-btn"
                    @click.stop="toggleMenu(guest.EventContactId, $event)"
                    :aria-label="t('hostDashboard.GuestActions')"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="5" r="2" fill="currentColor"/>
                      <circle cx="12" cy="12" r="2" fill="currentColor"/>
                      <circle cx="12" cy="19" r="2" fill="currentColor"/>
                    </svg>
                  </button>

                  <!-- Main Menu -->
                  <div
                    v-if="openMenuGuestId === guest.EventContactId && showStatusSubmenu !== guest.EventContactId"
                    class="menu-dropdown"
                  >
                    <button
                      class="menu-item"
                      @click="handleMenuAction('InvitationReminder', guest)"
                    >
                      {{ t('hostDashboard.InvitationReminder') }}
                    </button>

                    <button
                      class="menu-item menu-item-with-arrow"
                      @click.stop="showChangeStatus(guest.EventContactId)"
                    >
                      <span>{{ t('hostDashboard.ChangeStatus') }}</span>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" class="arrow-icon">
                        <path d="M9 18l6-6-6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>
                    </button>

                    <button
                      v-if="!guest.HideRemoval"
                      class="menu-item menu-item-danger"
                      @click="handleMenuAction('RemoveGuest', guest)"
                    >
                      {{ t('hostDashboard.RemoveGuest') }}
                    </button>
                  </div>

                  <!-- Status Submenu -->
                  <div
                    v-if="showStatusSubmenu === guest.EventContactId"
                    class="menu-dropdown"
                  >
                    <button
                      class="menu-item menu-back-item"
                      @click.stop="backToMainMenu()"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" class="back-icon">
                        <path d="M15 18l-6-6 6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>
                      <span>{{ t('hostDashboard.ChangeStatus') }}</span>
                    </button>

                    <div class="submenu-divider"></div>

                    <button
                      v-for="status in invitationStatuses"
                      :key="status.id"
                      class="menu-item"
                      @click="handleMenuAction('ChangeStatus', guest, status.id)"
                    >
                      {{ status.text }}
                    </button>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
          </table>
        </div>

        <!-- Pagination Controls -->
        <div v-if="viewModel?.Guests && viewModel.Guests.length > pageSize" class="guest-pagination-container">
          <component
            :is="customPagerRender"
            :total="viewModel?.Guests?.length || 0"
            :pageSize="pageSize"
            :skip="skip"
            :pageChange="onPageChange"
          />
        </div>
      </div>
    </div>

    <div v-if="showAddGuest" class="popup-overlay" @click="showAddGuest = false"></div>
    <div v-if="showImportGuests" class="popup-overlay" @click="closeImportGuestsHandler"></div>
    <Popup :show="showAddGuest" popup-class="host-dashboard-popup-content">
      <form @submit.prevent="addGuestSubmitClickHandler($event)" id="guestForm">
        <fieldset class="k-form-fieldset">
          <div class="row">
            <div class="col-12 form-field">
              <label class="form-label required">{{ t('columns.firstName') }}</label>
              <input
                v-model="addGuestInitialValues.firstName"
                type="text"
                class="form-input"
                :class="{ 'error': errors.firstName }"
                required
                @input="validateFirstName"
                @blur="validateFirstName"
              />
              <div v-if="errors.firstName" class="error-message">{{ errors.firstName }}</div>
            </div>
          </div>
          <div class="row">
            <div class="col-12 form-field">
              <label class="form-label required">{{ t('columns.lastName') }}</label>
              <input
                v-model="addGuestInitialValues.lastName"
                type="text"
                class="form-input"
                :class="{ 'error': errors.lastName }"
                required
                @input="validateLastName"
                @blur="validateLastName"
              />
              <div v-if="errors.lastName" class="error-message">{{ errors.lastName }}</div>
            </div>
          </div>
          <div class="row">
            <div class="col-12 form-field">
              <label class="form-label required">{{ t('columns.emailAddress') }}</label>
              <input
                v-model="addGuestInitialValues.emailAddress"
                type="email"
                class="form-input"
                :class="{ 'error': errors.emailAddress }"
                required
                @input="validateEmailAddress"
                @blur="validateEmailAddress"
                :disabled="hasNoEmail"
              />
              <div v-if="errors.emailAddress" class="error-message">{{ errors.emailAddress }}</div>
              <KCheckBox
                class="chbx-no-email mt-2"
                :modelValue="hasNoEmail"
                :label="t('eventManagement.NoEmail')"
                @change="noEmailCheckboxHandler($event)"
              />
            </div>
          </div>
          <div class="row">
            <div class="col-12 form-field">
              <label class="form-label">{{ t('columns.phone') }}</label>
              <PhoneInputControl
                ref="guestPhoneControl"
                class="guestPhoneField"
                v-model="guestPhoneModel"
                :required="false"
                :showExtension="false"
                :showPhoneType="false"
                :countryList="guestPhoneCountries"
              />
            </div>
          </div>
          <div class="row pt-4">
            <div class="col-6">
              <KButton
                ref="btnAddGuest"
                id="btnAddGuest"
                :rounded="'large'"
                :theme-color="'primary'"
                type="submit"
                >{{ t('hostDashboard.AddGuestButton') }}</KButton
              >
            </div>
            <div class="col-6">
              <KButton
                id="btnAddGuestCancel"
                :rounded="'large'"
                :theme-color="'secondary'"
                @click="showAddGuest = false"
                >{{ t('hostDashboard.AddGuestCancelButton') }}</KButton
              >
            </div>
          </div>
        </fieldset>
      </form>
    </Popup>

    <!-- Import Guests Popup -->
    <Popup :show="showImportGuests" popup-class="host-dashboard-import-popup-content">
      <div class="import-popup-header">
        <h3>{{ t('hostDashboard.ImportGuests') }}</h3>
        <button class="close-button" :aria-label="t('buttons.Close')" @click="closeImportGuestsHandler">{{ '\u00D7' }}</button>
      </div>
      <div class="import-popup-content">
        <FindEventContactsControl
                        :showAddGuest="true"
                        :eventidtoken="eventIdToken"
                        :beeNumberAuth="beeNumberAuth"
                        @guest-grid-refresh="refreshGuestGrid()"
                      ></FindEventContactsControl>
      </div>
    </Popup>
  </div>
</template>
<style lang="scss">
/* Custom dropdown menu styles */
.guest-actions-menu {
  position: relative;
}

.menu-trigger-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: $spacing-1;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: $radius-sm;
  color: #666;
  transition: all 0.2s;
}

.menu-trigger-btn:hover {
  background: var(--vt-c-white-soft);
  color: #333;
}

.menu-dropdown {
  position: absolute;
  right: 0;
  top: 100%;
  margin-top: 4px;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: $radius-md;
  box-shadow: 0 4px 12px rgba(black, 0.2);
  min-width: 200px;
  z-index: $z-notification;
  overflow: hidden;
}

.menu-dropdown-top {
  top: auto;
  bottom: 100%;
  margin-top: 0;
  margin-bottom: 4px;
}

.menu-item {
  display: block;
  width: 100%;
  padding: $spacing-2-5 $spacing-4;
  background: none;
  border: none;
  text-align: left;
  cursor: pointer;
  font-size: 14px;
  color: #333;
  transition: background 0.2s;
  border-bottom: 1px solid var(--vt-c-white-soft);
}

.menu-item:last-child {
  border-bottom: none;
}

.menu-item:hover {
  background: var(--vt-c-white-soft);
}

.menu-item-danger {
  color: #d32f2f;
}

.menu-item-danger:hover {
  background: #ffebee;
}

.menu-item-with-arrow {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.arrow-icon {
  color: #999999;
  transition: transform 0.2s;
}

.menu-item-with-arrow:hover .arrow-icon {
  color: #1976d2;
  transform: translateX(2px);
}

.menu-back-item {
  display: flex;
  align-items: center;
  gap: $gap-sm;
  font-weight: 600;
  color: #1976d2;
  background: var(--vt-c-white-soft);
}

.menu-back-item:hover {
  background: #e9ecef;
}

.back-icon {
  color: #1976d2;
}

.submenu-divider {
  height: 1px;
  background: #e0e0e0;
  margin: 0;
}

.menu-dropdown {
  max-height: 300px;
  overflow-y: auto;
}

.menu-dropdown-top {
  max-height: 300px;
}

.host-dashboard-popup-content {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  padding: $spacing-2-5;
  width: 400px;
  z-index: $z-modal;
  background: var(--vt-c-white);
  border-radius: $radius-lg;
  box-shadow: 0 4px 24px rgba(black, 0.1);

  @media (max-width: 768px) {
    width: 95%;
  }

  button {
    float: right;
    padding-top: $spacing-2-5;
    padding-bottom: $spacing-2-5;
  }
}

.popup-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(black, 0.3);
  z-index: $z-modal-backdrop;
}


.dashboard-cards {
  display: flex;
  gap: $spacing-6;
  margin-bottom: 32px;
  flex-wrap: wrap;
}

@media (max-width: 768px) {
  .dashboard-cards {
    flex-direction: column;
    gap: $spacing-5;
    margin-bottom: 24px;
  }
  .dashboard-card {
    min-width: 0;
    width: 100%;
    max-width: 100%;
    margin: 0 auto;
    box-sizing: border-box;
  }
}
.dashboard-card {
  flex: 1;
  background: var(--vt-c-white);
  border-radius: $radius-xl;
  box-shadow: 0 2px 12px rgba(black, 0.1);
  padding: $spacing-8 $spacing-8 $spacing-5 $spacing-8;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 220px;
}
.dashboard-card h4 {
  @include typography-body-large;
  @include font-weight-semibold;
  margin-bottom: 10px;
}
.dashboard-card .card-title {
  @include typography-body;
  color: #6e6e6e;
  margin-bottom: 6px;
}
.dashboard-card .card-value {
  @include typography-h2;
  margin-bottom: 6px;
}
.dashboard-card .card-desc {
  @include typography-body;
  color: #999999;
  margin-bottom: 0;
}
.status-chip {
  @include typography-body;
  @include font-weight-medium;
  display: inline-block;
  padding: $spacing-1 $spacing-4;
  border-radius: $radius-xl;
  color: var(--vt-c-white);
  background: #bdbdbd;
}
.status-chip.pending { background: #ffb74d; color: #7a4f01; }
.status-chip.going { background: #81c784; color: #1b5e20; }
.status-chip.cantgo { background: #e57373; color: #b71c1c; }

.guest-table {
  width: 100%;
  border-collapse: collapse;
}

@media (max-width: 768px) {
  .guest-table {
    display: block;
    width: 100%;
    border: none;
  }
  .guest-table thead {
    display: none;
  }
  .guest-table tbody {
    display: block;
    width: 100%;
  }
  .guest-table tr {
    display: block;
    background: var(--vt-c-white);
    border-radius: $radius-lg;
    box-shadow: 0 2px 8px rgba(black, 0.1);
    margin-bottom: 14px;
    padding: $spacing-3 $spacing-4;
    width: 100%;
  }
  .guest-table td {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: $spacing-2 0;
    border: none;
    @include typography-body;
    width: 100%;
  }
  .guest-table td:before {
    @include font-weight-semibold;
    content: attr(data-label);
    color: #888;
    flex: 1;
    text-align: left;
    margin-right: 12px;
    min-width: 110px;
    display: block;
  }

  .guest-table td.responsive-stacked {
    flex-direction: column;
    align-items: flex-start;
  }

  .guest-table tr {
    position: relative;
  }
  .guest-table td:first-child {
    padding-right: $spacing-10;
  }
  .guest-table td.guest-actions-cell {
    position: absolute;
    top: $spacing-2;
    right: $spacing-2;
    width: auto;
    padding: 0;
    border: none;
  }
  .guest-table td.guest-actions-cell:before {
    display: none;
  }
  .guest-table .menu-trigger-btn {
    background: var(--vt-c-white-soft);
    border: 1px solid var(--color-border);
    padding: $spacing-1 $spacing-2;
  }

  .guest-table .status-chip {
    margin-left: 0;
  }
}
.guest-table-wrapper {
  border-radius: $radius-lg;
  border: 1px solid #e0e0e0;
  box-shadow: 0 1px 8px rgba(black, 0.1);
  position: relative;
  background: var(--vt-c-white);
}

.guest-table {
  width: 100%;
  border-spacing: 0;
  border-collapse: separate;
  background: transparent;
  border: none;
  border-radius: $radius-lg;
}
.guest-table tbody {
  overflow: visible;
}
.guest-table tr {
  overflow: visible;
}
.guest-table th, .guest-table td {
  padding: $spacing-4 $spacing-5;
  text-align: left;
  border-bottom: 1px solid var(--vt-c-white-soft);
  border-right: none;
  overflow: visible;
  color: #333;
}

.guest-table th:not(:last-child), .guest-table td:not(:last-child) {
  border-right: none;
}
.guest-table th {
  @include typography-body;
  @include font-weight-semibold;
  background: #fefefe;
  color: #757575;
}

/* Round top corners */
.guest-table thead tr:first-child th:first-child {
  border-top-left-radius: 11px;
}
.guest-table thead tr:first-child th:last-child {
  border-top-right-radius: 11px;
}

/* Round bottom corners */
.guest-table tbody tr:last-child td:first-child {
  border-bottom-left-radius: 11px;
}
.guest-table tbody tr:last-child td:last-child {
  border-bottom-right-radius: 11px;
}

@media (min-width: 769px) {
  .guest-table tr:last-child td {
    border-bottom: none;
  }
}
.guest-table tr:nth-child(even) {
  background: #fefefe;
}
.btnAddGuest {
  background: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
  @include typography-body;
  @include font-weight-semibold;
  color: var(--vt-c-white);
  border-radius: $radius-2xl;
  padding: $spacing-2 $spacing-6;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: background 0.2s;
}
.btnAddGuest:hover {
  filter: brightness(0.85);
}

.host-dashboard {
  padding: $spacing-10 0 0 0;
}

.party-details-card {
  background: var(--vt-c-white);
  border-radius: $radius-lg;
  box-shadow: 0 1px 8px rgba(black, 0.1);
  border: 1px solid #e9ecef;
  padding: $spacing-6 $spacing-5 $spacing-5 $spacing-5;
  margin-left: auto;
  margin-right: auto;
  text-align: center;
}
.party-title {
  @include typography-body-large;
  @include font-weight-semibold;
  margin-bottom: 2px;
  color: #444;
}
.party-event-name {
  @include typography-body;
  @include font-weight-semibold;
  margin-bottom: 12px;
  color: teal;
}
.party-date-time-row {
  margin-bottom: 10px;
}
.welcome-section {
  margin-bottom: 28px;
  text-align: left;
}
.welcome-title {
  @include typography-body-large;
  @include font-weight-semibold;
  margin-bottom: 4px;
  color: #222;
}
.welcome-subtitle {
  @include typography-body;
  color: #7e7e7e;
}
.guest-list-header {
  @include typography-h4;
  @include font-weight-bold;
  color: #222;
  margin-bottom: 12px;
  text-align: left;
}

.guest-list-header-buttons {
  display: flex;
  gap: $spacing-2;
  align-items: center;
  justify-content: flex-end;
}

@media (max-width: 575px) {
  .guest-list-header-buttons {
    flex-direction: column;
    align-items: stretch;
  }
}

.guest-list-container {
  overflow: visible !important;
}

.host-dashboard-import-popup-content {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 90vw;
  max-width: 800px;
  max-height: 80vh;
  overflow-y: auto;
  background: white;
  border-radius: $radius-lg;
  box-shadow: 0 10px 40px rgba(black, 0.2);
  z-index: $z-notification;
}

.import-popup-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: $spacing-5 $spacing-6;
  border-bottom: 1px solid #e0e0e0;
}

.import-popup-header h3 {
  @include typography-h4;
  @include font-weight-semibold;
  margin: 0;
  color: #333;
}

.close-button {
  @include typography-h3;
  background: none;
  border: none;
  cursor: pointer;
  color: #666;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background-color 0.2s;
}

.close-button:hover {
  background-color: var(--vt-c-white-soft);
}

.import-popup-content {
  padding: $spacing-6;
}

.k-button.btnImportGuests {
  background: white;
  color: #333;
  border: 1px solid #e0e0e0;
  padding: $spacing-4 $spacing-8;
  border-radius: $radius-md;
  @include typography-body;
  @include font-weight-medium;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 3px rgba(black, 0.1);
}

.k-button.btnImportGuests:hover {
  background: var(--vt-c-white-soft);
  border-color: #bdbdbd;
  box-shadow: 0 2px 5px rgba(black, 0.2);
}

.order-status-chip {
  padding: $spacing-1 $spacing-2;
  border-radius: $radius-sm;
  font-size: 12px;
  font-weight: 500;
  text-transform: uppercase;
}

.order-submitted {
  background-color: #e8f5e9;
  color: #2e7d32;
  border: 1px solid #c3e6cb;
}

.order-pending {
  background-color: #fff3e0;
  color: #f57c00;
  border: 1px solid #ffcc02;
}

.sales-breakdown {
  margin: $spacing-3 0;
  padding: $spacing-4;
  background-color: var(--vt-c-white-soft);
  border-radius: $radius-md;
  border: 1px solid #e9ecef;
  width: 100%;
}

.breakdown-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 8px;
  margin-bottom: 8px;
  min-height: 24px;
  border-bottom: 1px solid #dee2e6;
}

.breakdown-item:last-child {
  margin-bottom: 0;
  padding-bottom: 0;
  border-bottom: none;
}

.breakdown-label {
  font-size: 0.95rem;
  color: #666;
  font-weight: 500;
  flex: 1;
}

.breakdown-value {
  font-size: 0.95rem;
  font-weight: 600;
  text-align: right;
  min-width: 80px;
  font-family: 'Courier New', monospace;
}

.breakdown-value.submitted {
  color: #2e7d32;
}

.breakdown-value.pending {
  color: #f57c00;
}

.party-date-time-pill {
  display: inline-flex;
  align-items: center;
  border-radius: $radius-md;
  background: #f6ece2;
  color: #6a4d34;
  font-size: 0.98rem;
  font-weight: 500;
  padding: $spacing-1 $spacing-3;
}
.party-date, .party-time {
  background: none;
  color: #6a4d34;
  padding: 0;
  margin: 0;
}
.party-date-divider {
  display: inline-block;
  width: 1px;
  height: 18px;
  background: #e0c9b1;
  margin: 0 10px;
  border-radius: $radius-xs;
}

.party-pill {
  display: inline-block;
  border-radius: $radius-md;
  padding: $spacing-1 $spacing-3;
  margin: 0 3px;
  font-size: 0.98rem;
  background: #f6ece2;
  color: #6a4d34;
  font-weight: 500;
}
.party-date {
  background: #f6ece2;
  color: #6a4d34;
}
.party-time {
  background: #e0e0e0;
  color: #4e5c6e;
}
.party-location {
  color: #7e7e7e;
  font-size: 0.97rem;
}
.party-location-pin {
  display: inline-block;
  vertical-align: middle;
}

.party-stats-row {
  display: flex;
  justify-content: center;
  gap: $spacing-8;
  margin-top: 10px;
}
.party-stat {
  text-align: center;
}
.party-stat-label {
  font-size: 0.93rem;
  color: #757575;
  margin-bottom: 2px;
}
.party-invited {
  background: #e9ecef;
  color: #3a6e7c;
  font-weight: 600;
  font-size: 1.1rem;
}
.party-going {
  background: #e8f5e9;
  color: #3a7c4a;
  font-weight: 600;
  font-size: 1.1rem;
}
.party-divider {
  border: none;
  border-top: 1px solid #bdbdbd;
  margin: $spacing-5 0 $spacing-3 0;
  width: 100%;
  display: block;
}

/* Guest Table Pagination Styles */
.guest-pagination-container {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: $spacing-5 0;
  margin-top: 16px;
  border-top: 1px solid var(--vt-c-white-soft);
}

.guest-pager-bar {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: $spacing-1;
  flex-wrap: wrap;
}

.guest-pager-btn {
  background: var(--vt-c-white-soft);
  border: 1px solid #e0e0e0;
  color: #333;
  padding: $spacing-2 $spacing-3;
  margin: 0 2px;
  border-radius: $radius-md;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 500;
  transition: all 0.2s ease;
  min-width: 40px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
}

.guest-pager-btn.guest-pager-nav {
  font-size: 1.1rem;
  font-weight: 600;
  min-width: 36px;
}

.guest-pager-btn.k-selected, .guest-pager-btn:disabled {
  background: #1976d2;
  color: white;
  border: 1px solid #1976d2;
  cursor: default;
  box-shadow: 0 2px 4px rgba(#1976d2, 0.2);
}

.guest-pager-btn.disabled {
  background: var(--vt-c-white-soft);
  color: #cccccc;
  border: 1px solid #e0e0e0;
  cursor: not-allowed;
}

.guest-pager-btn:not(.k-selected):not(:disabled):not(.disabled):hover {
  background: #e3f2fd;
  color: #1976d2;
  border-color: #1976d2;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(#1976d2, 0.1);
}

.guest-pager-ellipsis {
  color: #999999;
  font-size: 1.2rem;
  padding: 0 8px;
  display: flex;
  align-items: center;
  height: 36px;
}

@media (max-width: 768px) {
  .guest-pagination-container {
    padding: $spacing-4 0;
  }

  .guest-pager-btn {
    padding: $spacing-2 $spacing-2-5;
    font-size: 0.85rem;
    min-width: 36px;
    height: 32px;
  }

  .guest-pager-btn.guest-pager-nav {
    min-width: 32px;
  }

  .guest-pager-ellipsis {
    padding: 0 6px;
    height: 32px;
  }
}

/* ======== Total Sales Card ======== */
.total-sales-card {
  text-align: center;
}

.donut-chart-wrapper {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin: $spacing-4 0 $spacing-3;
}

.donut-svg {
  display: block;
}

.donut-center-text {
  position: absolute;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.donut-label {
  @include typography-caption;
  color: #888;
  line-height: 1.2;
}

.donut-value {
  @include typography-h4;
  @include font-weight-bold;
  color: #c85a2a;
  line-height: 1.1;
}

.next-level-message {
  @include typography-small;
  color: #555;
  text-align: center;
  padding: 0 $spacing-4;
  margin-bottom: $spacing-3;
  line-height: 1.5;
}

.next-level-message.next-level-complete {
  color: #2e7d32;
}

.about-rewards-link {
  @include typography-small;
  color: var(--color-teal, #477981);
  text-decoration: underline;
  cursor: pointer;
  display: inline-block;
  margin-top: auto;
  padding-top: $spacing-2;
}

.about-rewards-link:hover {
  opacity: 0.8;
}

/* ======== My Rewards Card ======== */
.my-rewards-card {
  text-align: center;
}

.reward-level-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: $spacing-4;
}

.reward-level-label {
  @include typography-small;
  color: #888;
  margin-bottom: $spacing-1;
}

.reward-level-name {
  @include typography-h4;
  @include font-weight-semibold;
  color: var(--color-teal, #477981);
}

.reward-tiles {
  display: flex;
  flex-direction: column;
  gap: $spacing-2;
  width: 100%;
  margin-bottom: $spacing-3;
}

.reward-tile {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: $spacing-3;
  padding: $spacing-2 $spacing-3;
  background: var(--vt-c-white-soft);
  border-radius: $radius-md;
  border: 1px solid #e9ecef;
  flex-shrink: 0;
}

.reward-tile-icon {
  color: #555;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.reward-tile.FreeProduct .reward-tile-icon,
.reward-tile.SelectFreeProduct .reward-tile-icon { color: #2e7d32; }

.reward-tile.FreeDollars .reward-tile-icon,
.reward-tile.FreeDollarsOffOrder .reward-tile-icon { color: #1976d2; }

.reward-tile.FreeShipping .reward-tile-icon { color: #f57c00; }

.reward-tile.PercentageOffItem .reward-tile-icon,
.reward-tile.PercentageOffOrder .reward-tile-icon { color: #7b1fa2; }

.reward-tile.Coupon .reward-tile-icon { color: #c85a2a; }

.reward-tile.DiscountedItem .reward-tile-icon,
.reward-tile.DiscountedSkuFromList .reward-tile-icon { color: #00796b; }

.reward-tile-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
}

.reward-tile-label {
  @include typography-caption;
  color: #666;
  line-height: 1.3;
}

.reward-tile-value {
  @include typography-small;
  @include font-weight-semibold;
  color: #333;
}

.reward-tile-qty {
  color: var(--color-teal, #477981);
}

.rewards-shop-buttons {
  display: flex;
  flex-direction: row;
  gap: $spacing-3;
  justify-content: center;
  flex-wrap: wrap;
  margin-top: auto;
  width: 100%;
}

.rewards-shop-btn {
  @include typography-small;
  @include font-weight-semibold;
  padding: $spacing-2 $spacing-4;
  border-radius: $radius-pill;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 120px;
}

/* No rewards state */
.no-rewards {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: $spacing-8 $spacing-4;
  text-align: center;
}

.no-rewards-text {
  @include typography-small;
  color: #999999;
  margin: 0;
  font-style: italic;
}

/* Password Prompt Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.password-modal {
  background: var(--vt-c-white);
  padding: $spacing-6;
  border-radius: $radius-lg;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  min-width: 400px;
  max-width: 90%;
  z-index: 10000;
}

.password-modal h3 {
  margin: 0 0 $spacing-4 0;
  @include typography-h3;
}

.password-modal p {
  margin: 0 0 $spacing-4 0;
  @include typography-body;
  color: var(--color-text);
}

.password-modal .k-textbox {
  width: 100%;
  margin-bottom: $spacing-4;
  padding: $spacing-3;
}

.modal-buttons {
  display: flex;
  justify-content: flex-end;
  gap: $spacing-3;
}

// Form field styling (matches AoeEventManagement)
.form-field {
  display: flex;
  flex-direction: column;
  margin-bottom: $spacing-2;
}

.form-label {
  @include typography-body;
  @include font-weight-semibold;
  margin-bottom: $spacing-1;
}

.form-label.required::after {
  content: ' *';
  color: #dc3545;
}

.form-input {
  padding: $spacing-3;
  border: 1px solid var(--color-border);
  border-radius: $radius-md;
  @include typography-body;
  transition: border-color 0.2s;
}

.form-input:focus {
  outline: none;
  border-color: var(--color-teal);
}

.form-input.error {
  border-color: #dc3545;
}

.form-input.error:focus {
  border-color: #dc3545;
}

.form-field .error-message {
  @include typography-small;
  color: #dc3545;
  margin-top: $spacing-1;
  font-style: normal !important;
}

// Match PhoneInputControl to Kendo rounded large inputs
.guestPhoneField .phone-input-group {
  width: 100%;
}

.guestPhoneField .country-select,
.guestPhoneField .phone-input {
  @include typography-small;
  padding-top: $spacing-1;
  padding-bottom: $spacing-1;
}

.guestPhoneField .country-select {
  border-radius: $radius-md 0 0 $radius-md;
}

.guestPhoneField .phone-input {
  /* !important required: PhoneInputControl's scoped .phone-input declares border-radius with !important */
  border-radius: 0 $radius-md $radius-md 0 !important;
}

// Ensure phone error message is not italic to match other fields
.guestPhoneField .error-message {
  font-style: normal !important;
}
</style>
